import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatCard, inr } from "@/components/platform/platformUi";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return "—";
    }
}

export default function PlatformRevenue() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/platform/revenue")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Could not load revenue"));
    }, []);

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-revenue-page">
            <PageHeader
                eyebrow="Revenue"
                title="Platform revenue"
                subtitle="Your SaaS income from salon subscriptions — separate from salon POS turnover"
            />

            {data && (
                <>
                    <div>
                        <p className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#C9A227]">Platform income (your revenue)</p>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <StatCard label="MRR" value={inr(data.platform?.mrr)} sub="Active subscriptions" accent="#FBE4EF" iconColor="#D9468F" />
                            <StatCard label="ARR" value={inr(data.platform?.arr)} />
                            <StatCard label="Last 30 days" value={inr(data.platform?.revenue_30d)} />
                            <StatCard label="All time" value={inr(data.platform?.revenue_all_time)} />
                        </div>
                    </div>

                    <section className="rounded-xl border border-[#E8E4DE] bg-white p-5">
                        <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">Revenue by plan</p>
                        <div className="mt-3 overflow-x-auto">
                            <table className="min-w-full border-collapse text-sm">
                                <thead>
                                    <tr className="border-b border-[#E8E4DE] text-left text-[11px] font-extrabold uppercase text-[#8A8178]">
                                        <th className="py-2 pr-4">Plan</th>
                                        <th className="py-2">Collected</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.platform?.by_plan || []).map((row) => (
                                        <tr key={row.plan} className="border-b border-[#E8E4DE]/40">
                                            <td className="py-2.5 pr-4 font-semibold text-[#4A0E17]">{row.plan}</td>
                                            <td className="py-2.5 font-bold text-[#3D2817]">{inr(row.revenue)}</td>
                                        </tr>
                                    ))}
                                    {(data.platform?.by_plan || []).length === 0 && (
                                        <tr><td colSpan={2} className="py-6 text-center text-[#8A8178]">No subscription payments yet</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <div>
                        <p className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">Salon POS volume (not your income)</p>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <StatCard label="Salon POS (30d)" value={inr(data.salon_pos?.revenue_30d)} sub={data.salon_pos?.note} accent="#E0EDFA" iconColor="#3B82C4" />
                            <StatCard label="Salon POS (all time)" value={inr(data.salon_pos?.revenue_all_time)} sub="Processed via each salon's Razorpay" />
                        </div>
                    </div>

                    <section className="space-y-3">
                        <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">Recent subscription payments</p>
                        <div className="overflow-x-auto rounded-xl border border-[#E8E4DE] bg-white">
                            <table className="min-w-max w-full border-collapse">
                                <thead>
                                    <tr className="border-b border-[#E8E4DE] text-left text-[11px] font-extrabold uppercase text-[#8A8178]">
                                        <th className="px-4 py-3">Date</th>
                                        <th className="px-4 py-3">Salon</th>
                                        <th className="px-4 py-3">Plan</th>
                                        <th className="px-4 py-3">Amount</th>
                                        <th className="px-4 py-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.recent_payments || []).map((r) => (
                                        <tr key={r.id} className="border-b border-[#E8E4DE]/50 last:border-b-0">
                                            <td className="px-4 py-3 text-sm text-[#8A8178]">{formatDate(r.paid_at || r.created_at)}</td>
                                            <td className="px-4 py-3">
                                                <Link to={`/platform/salons/${r.tenant_id}`} className="font-semibold text-[#4A0E17] hover:text-[#C9A227]">
                                                    {r.tenant?.name || "—"}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-[#3D2817]">{r.plan?.name || "—"}</td>
                                            <td className="px-4 py-3 text-sm font-bold text-[#4A0E17]">{inr(r.amount)}</td>
                                            <td className="px-4 py-3 text-sm capitalize text-[#3D2817]">{r.status}</td>
                                        </tr>
                                    ))}
                                    {(data.recent_payments || []).length === 0 && (
                                        <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-[#8A8178]">No payments recorded</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
